import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { callGetLegacyLifeEntryList } from "../../api/legacy-life-entry/service";
import SmartTable from "../../components/common/table-component";
import "../../components/i18n/lang";
import "./legacy-life-entry.css";
import { dateToString } from "../../utils/commonUtil";

const STATUS_OPTIONS = ["", "DRAFT", "PENDING", "APPROVED", "REJECTED"];
const DEFAULT_REQUEST = { page: 1, maxResult: 10, keyword: "" };

export default function LegacyList({ approval = false }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [tableData, setTableData] = useState({ result: [], totalPages: 0, totalRecords: 0 });
  const [status, setStatus] = useState(approval ? "PENDING" : "");
  const [request, setRequest] = useState(DEFAULT_REQUEST);

  const loadData = useCallback(async (next = {}) => {
    const query = { ...request, ...next, status: approval ? "PENDING" : status };
    setRequest(prev => ({ ...prev, ...next }));
    const rsp = await dispatch(callGetLegacyLifeEntryList(query));
    if (rsp?.payload?.responseCode === "00000") {
      const data = rsp?.payload?.data || {};
      setTableData({
        result: Array.isArray(data.result) ? data.result : [],
        totalPages: Number(data.totalPages || 0),
        totalRecords: Number(data.totalRecords || 0),
      });
    } else {
      setTableData({ result: [], totalPages: 0, totalRecords: 0 });
    }
  }, [dispatch, request, status, approval]);

  useEffect(() => {
    loadData({ page: 1 });
    // status/approval are the list-level filters; pagination calls loadData directly.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, approval]);

  const columns = useMemo(() => [
    { label: t("legacyLifeEntry.requestType"), key: "requestType", render: value => t(`legacyLifeEntry.requestTypeText.${value || "CONTRACT"}`) },
    { label: t("legacyLifeEntry.contractNumber"), key: "contractNumber" },
    { label: t("legacyLifeEntry.insured"), key: "customerName" },
    { label: t("legacyLifeEntry.billNumber"), key: "billNumber", render: value => value || "-" },
    { label: t("legacyLifeEntry.createdAt"), key: "createdAt", render: value => dateToString(value,'DD/MM/YYYY hh:mm:ss') },
    {
      label: t("legacyLifeEntry.status"), key: "status", className: "text-center",
      render: value => <span className={`legacy-badge ${value}`}>{t(`legacyLifeEntry.statusText.${value}`)}</span>,
    },
  ], [t]);

  const handleView = item => {
    const requestType = String(item.requestType || "CONTRACT").toUpperCase();
    // Detail pages are read-only after submission/approval. Never route View back to entry/edit pages.
    if (requestType === "BILL") {
      navigate(`/admin/legacyLifeEntry/approve-bill/${item.id}`, { state: { fromList: true, approval } });
      return;
    }
    navigate(`/admin/legacyLifeEntry/approve/${item.id}`, { state: { fromList: true, approval } });
  };

  return (
    <div className="legacy-wrap">
      <div className="legacy-head legacy-list-head">
        <div>
          <h3>{t(approval ? "legacyLifeEntry.approvalListTitle" : "legacyLifeEntry.listTitle")}</h3>
          <p>{t("legacyLifeEntry.listSearchHint")}</p>
        </div>
        {!approval && <div className="legacy-list-create-actions">
          <button className="legacy-btn-secondary" onClick={() => navigate("/admin/legacyLifeEntry/bill", { state: { fromList: true } })}>+ {t("legacyLifeEntry.createBill")}</button>
          <button className="legacy-btn-primary" onClick={() => navigate("/admin/legacyLifeEntry/save", { state: { fromList: true } })}>+ {t("legacyLifeEntry.createContract")}</button>
        </div>}
      </div>

      <div className="legacy-list-toolbar">
        <div className="legacy-list-search">
          <span>⌕</span>
          <input
            value={request.keyword}
            onChange={e => setRequest(prev => ({ ...prev, keyword: e.target.value }))}
            onKeyDown={e => e.key === "Enter" && loadData({ page: 1, keyword: request.keyword })}
            placeholder={t("legacyLifeEntry.listSearchPlaceholder")}
          />
          {request.keyword && <button type="button" onClick={() => loadData({ page: 1, keyword: "" })}>×</button>}
          <button type="button" className="legacy-btn-primary" onClick={() => loadData({ page: 1, keyword: request.keyword })}>{t("legacyLifeEntry.searchButton")}</button>
        </div>
        <div className="legacy-list-count"><strong>{Number(tableData.totalRecords || 0).toLocaleString()}</strong><span>{t("legacyLifeEntry.records")}</span></div>
      </div>

      {!approval && <div className="legacy-filters">
        {STATUS_OPTIONS.map(item => (
          <button key={item || "ALL"} className={status === item ? "on" : ""} onClick={() => setStatus(item)}>
            {item ? t(`legacyLifeEntry.statusText.${item}`) : t("legacyLifeEntry.all")}
          </button>
        ))}
      </div>}

      <SmartTable
        columns={columns}
        data={tableData}
        rowKey="id"
        recordsTableFilter={true}
        customPagination={true}
        initialPage={request.page}
        initialMaxResult={request.maxResult}
        keyword={request.keyword}
        onLazyLoad={loadData}
        showRowNumber={true}
        actions={item => {
          const requestType = String(item.requestType || "CONTRACT").toUpperCase();
          const canEditDraft = !approval && requestType === "CONTRACT" && item.status === "DRAFT";
          const canEditRejected = !approval && item.status === "REJECTED";
          return (
            <div className="legacy-row-actions">
              <button onClick={() => handleView(item)}>{t("legacyLifeEntry.view")}</button>
              {canEditDraft && (
                <button
                  className="legacy-edit-draft"
                  onClick={() => navigate(`/admin/legacyLifeEntry/save/${item.id}`, { state: { fromList: true, editDraft: true } })}
                >
                  {t("legacyLifeEntry.editDraftAndSubmit")}
                </button>
              )}
              {canEditRejected && (
                <button
                  className="legacy-edit-rejected"
                  onClick={() => navigate(requestType === "BILL" ? `/admin/legacyLifeEntry/bill/${item.id}` : `/admin/legacyLifeEntry/save/${item.id}`, { state: { fromList: true } })}
                >
                  {t("legacyLifeEntry.editAndResubmit")}
                </button>
              )}
            </div>
          );
        }}
      />
    </div>
  );
}
